import { ViewportScroller } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, finalize, of } from 'rxjs';
import { Translation } from '../../../../core/i18n/translation.service';
import type { WebsiteContactInfoDto } from '../../../../core/models/website-content/website-content.models';
import { LandingAssistantService } from '../../../../core/services/landing-assistant.service';
import { SeoMetaService } from '../../../../core/services/seo-meta.service';
import { WebsiteContentService } from '../../../../core/services/website-content.service';
import { TokenStorageService } from '../../../../core/storage/token-storage.service';
import { PublicShellLayout } from '../../../../shared/components/public-shell-layout/public-shell-layout';
import { ValidationMessage } from '../../../../shared/components/validation-message/validation-message';
import { WnRequiredFieldMarkerDirective } from '../../../../shared/directives/wn-required-field-marker.directive';

import { WnFocusInvalidOnSubmitDirective } from '../../../../shared/directives/wn-focus-invalid-on-submit.directive';
/**
 * The testimonial block ships with the design's sample quote. Keep it hidden until a real
 * customer quote (name, role, figures) replaces the placeholder copy in the i18n files.
 */
const SHOW_TESTIMONIAL = false;

@Component({
  selector: 'app-home-page',
  standalone: true,
  imports: [WnFocusInvalidOnSubmitDirective, PublicShellLayout, TranslatePipe, RouterLink, ReactiveFormsModule, ValidationMessage, WnRequiredFieldMarkerDirective],
  templateUrl: './home-page.html',
  styleUrl: './home-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomePage implements OnInit {
  private readonly seoMeta = inject(SeoMetaService);
  private readonly tokens = inject(TokenStorageService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly translation = inject(Translation);
  private readonly leads = inject(LandingAssistantService);
  private readonly websiteContent = inject(WebsiteContentService);

  /** Primary CTAs switch from sign-in to workspace when a session exists. */
  protected readonly isSignedIn = signal(this.tokens.hasValidTokens());
  protected readonly showTestimonial = SHOW_TESTIMONIAL;

  // --- Live data -----------------------------------------------------------
  protected readonly contact = signal<WebsiteContactInfoDto | null>(null);

  protected readonly phoneHref = computed(() => {
    const raw = this.contact()?.phoneNumber?.trim();
    return raw ? `tel:${raw.replace(/[^+\d]/g, '')}` : '';
  });

  // --- Demo request form ----------------------------------------------------
  protected readonly employeeBands = ['1-50', '51-200', '201-500', '500+'] as const;
  protected readonly painOptions = ['ATTENDANCE', 'PAYROLL', 'APPROVALS', 'LEAVE', 'REPORTING'] as const;
  protected readonly selectedPains = signal<ReadonlySet<string>>(new Set());
  protected readonly demoSubmitting = signal(false);
  protected readonly demoSubmitted = signal(false);
  protected readonly demoError = signal<string | null>(null);

  protected readonly demoForm = this.fb.group({
    name: this.fb.control('', { validators: [Validators.required, Validators.maxLength(120)] }),
    email: this.fb.control('', { validators: [Validators.required, Validators.email] }),
    phone: this.fb.control('', { validators: [Validators.required, Validators.minLength(7), Validators.maxLength(25)] }),
    company: this.fb.control('', { validators: [Validators.required, Validators.maxLength(120)] }),
    employees: this.fb.control<string>('51-200'),
    honeypot: this.fb.control(''),
  });

  // --- Static, translated content -----------------------------------------
  protected readonly heroChecks = ['LANDING.HERO.CHECK_1', 'LANDING.HERO.CHECK_2', 'LANDING.HERO.CHECK_3'] as const;
  protected readonly heroStats = [1, 2, 3, 4].map((i) => ({ value: `LANDING.HERO.STAT_${i}_VALUE`, label: `LANDING.HERO.STAT_${i}_LABEL` }));

  protected readonly sectors = [
    { icon: 'pi pi-wrench', key: 'LANDING.SECTORS.CONTRACTING' },
    { icon: 'pi pi-shopping-bag', key: 'LANDING.SECTORS.RETAIL' },
    { icon: 'pi pi-truck', key: 'LANDING.SECTORS.LOGISTICS' },
    { icon: 'pi pi-building', key: 'LANDING.SECTORS.FACILITIES' },
  ] as const;

  protected readonly outcomes = [
    { icon: 'pi pi-clock', key: 'ATTENDANCE' },
    { icon: 'pi pi-wallet', key: 'PAYROLL' },
    { icon: 'pi pi-share-alt', key: 'APPROVALS' },
  ] as const;

  protected readonly modules = [
    { titleKey: 'LANDING.MODULES.ATTENDANCE_TITLE', detailKey: 'LANDING.MODULES.ATTENDANCE_DETAIL', icon: 'pi pi-clock' },
    { titleKey: 'LANDING.MODULES.PAYROLL_TITLE', detailKey: 'LANDING.MODULES.PAYROLL_DETAIL', icon: 'pi pi-wallet' },
    { titleKey: 'LANDING.MODULES.WORKFLOWS_TITLE', detailKey: 'LANDING.MODULES.WORKFLOWS_DETAIL', icon: 'pi pi-share-alt' },
    { titleKey: 'LANDING.MODULES.RECRUITMENT_TITLE', detailKey: 'LANDING.MODULES.RECRUITMENT_DETAIL', icon: 'pi pi-briefcase' },
    { titleKey: 'LANDING.MODULES.ROLES_TITLE', detailKey: 'LANDING.MODULES.ROLES_DETAIL', icon: 'pi pi-shield' },
    { titleKey: 'LANDING.MODULES.REPORTS_TITLE', detailKey: 'LANDING.MODULES.REPORTS_DETAIL', icon: 'pi pi-chart-bar' },
  ] as const;

  protected readonly compliancePoints = [1, 2, 3, 4, 5].map((i) => `LANDING.COMPLIANCE.POINT_${i}`);

  /** Illustrative attendance feed for the "what a Monday looks like" preview (sample data, not live). */
  protected readonly feed = [
    { initials: 'ON', nameKey: 'LANDING.FEED.ROW_1_NAME', detailKey: 'LANDING.FEED.ROW_1_DETAIL', statusKey: 'LANDING.FEED.STATUS_ON_TIME', tone: 'success' },
    { initials: 'MA', nameKey: 'LANDING.FEED.ROW_2_NAME', detailKey: 'LANDING.FEED.ROW_2_DETAIL', statusKey: 'LANDING.FEED.STATUS_IN_REVIEW', tone: 'warning' },
    { initials: 'DF', nameKey: 'LANDING.FEED.ROW_3_NAME', detailKey: 'LANDING.FEED.ROW_3_DETAIL', statusKey: 'LANDING.FEED.STATUS_LATE', tone: 'danger' },
    { initials: 'SE', nameKey: 'LANDING.FEED.ROW_4_NAME', detailKey: 'LANDING.FEED.ROW_4_DETAIL', statusKey: 'LANDING.FEED.STATUS_ON_TIME', tone: 'success' },
  ] as const;

  protected readonly steps = [1, 2, 3].map((i) => ({
    n: i,
    titleKey: `LANDING.HOW_IT_WORKS.STEP${i}_TITLE`,
    detailKey: `LANDING.HOW_IT_WORKS.STEP${i}_DETAIL`,
    whenKey: `LANDING.HOW_IT_WORKS.STEP${i}_WHEN`,
  }));

  protected readonly resultStats = [1, 2, 3, 4].map((i) => ({ value: `LANDING.RESULTS.STAT_${i}_VALUE`, label: `LANDING.RESULTS.STAT_${i}_LABEL` }));

  protected readonly faqs = [1, 2, 3, 4, 5, 6].map((i) => ({ q: `LANDING.FAQ.Q${i}`, a: `LANDING.FAQ.A${i}` }));

  constructor() {
    // Section links scroll below the sticky marketing header (64px) with a little breathing room.
    inject(ViewportScroller).setOffset([0, 80]);
  }

  ngOnInit(): void {
    this.seoMeta.setPageMeta({
      title: 'WorkNest HR',
      description:
        'WorkNest brings attendance, leave, payroll and approvals into one bilingual HR workspace for Saudi and GCC teams, with branch-level policies and a full audit trail.',
      path: '/',
    });

    this.websiteContent
      .getContactInfo()
      .pipe(catchError(() => of(null)))
      .subscribe((contact) => this.contact.set(contact?.isPublished === false ? null : contact));

  }

  protected isPainSelected(key: string): boolean {
    return this.selectedPains().has(key);
  }

  protected togglePain(key: string): void {
    this.selectedPains.update((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  protected submitDemo(): void {
    if (this.demoForm.invalid) {
      this.demoForm.markAllAsTouched();
      return;
    }
    const value = this.demoForm.getRawValue();
    const pains = [...this.selectedPains()].map((key) => this.translation.instant(`LANDING.DEMO.PAIN_${key}`)).join(', ');
    const context = [
      this.translation.instant('LANDING.DEMO.CONTEXT_PREFIX'),
      `${this.translation.instant('LANDING.DEMO.EMPLOYEES')}: ${value.employees}`,
      pains ? `${this.translation.instant('LANDING.DEMO.PAIN_LABEL')}: ${pains}` : '',
    ]
      .filter(Boolean)
      .join(' · ');

    this.demoError.set(null);
    this.demoSubmitting.set(true);
    this.leads
      .submitLead({
        name: value.name.trim(),
        email: value.email.trim(),
        phone: value.phone.trim(),
        company: value.company.trim(),
        locale: this.translation.getCurrentLang(),
        conversationContext: context,
        honeypot: value.honeypot || undefined,
      })
      .pipe(finalize(() => this.demoSubmitting.set(false)))
      .subscribe({
        next: () => this.demoSubmitted.set(true),
        error: () => this.demoError.set(this.translation.instant('LANDING.DEMO.ERROR')),
      });
  }
}
